/**
 * Prompt Enhancement Configuration
 * ==================================
 * Single source of truth for LLM-based prompt enhancement system prompts.
 * 
 * Each module with a prompt input has its configuration here.
 * System prompts are auto-generated using promptEnhancementGenerator.js
 * but can be manually edited for fine-tuning.
 * 
 * Structure:
 * - moduleId: Unique identifier matching the module
 * - systemPrompt: LLM instruction for enhancing prompts
 * - model: OpenRouter model to use
 * - temperature: Creativity level (0-2)
 * - maxTokens: Maximum response length
 */

import { generateSystemPromptByUseCase } from './promptEnhancementGenerator.js';
import { DEFAULT_ENHANCEMENT_MODEL } from './promptEnhancementDefaults.js';
import {
  buildChosenMovementNote,
  buildMotionNote,
  buildReplyShape,
  buildShotContractBody,
  buildShotContractRules,
  SHOT_CONTRACT_PROSE_FIELDS,
} from './promptEnhancementShotContract.js';

export { DEFAULT_ENHANCEMENT_MODEL } from './promptEnhancementDefaults.js';

function buildContextNotes(context) {
  // Support both new role-based and legacy context shapes
  const notes = [];

  // New format: roleInsights = { subject: {0: desc}, style: {0: desc}, scene: {0: desc} }
  if (context?.roleInsights) {
    const roleLabels = { subject: 'SUBJECT', style: 'STYLE', scene: 'SCENE', structure: 'STRUCTURE' };
    for (const [role, label] of Object.entries(roleLabels)) {
      const insights = context.roleInsights[role];
      if (insights && Object.keys(insights).length > 0) {
        const formatted = Object.entries(insights)
          .map(([idx, desc]) => `${label.charAt(0) + label.slice(1).toLowerCase()} ${parseInt(idx)+1}: ${desc}`)
          .join(', ');
        notes.push(`\n${label} CONTEXT: ${formatted}`);
      }
    }
  }

  // Legacy format fallback: visionInsights + styleInsights
  if (notes.length === 0) {
    if (context?.visionInsights && Object.keys(context.visionInsights).length > 0) {
      const insights = Object.entries(context.visionInsights)
        .map(([idx, desc]) => `Ref ${parseInt(idx)+1}: ${desc}`)
        .join(', ');
      notes.push(`\nREFERENCE CONTEXT: ${insights}`);
    }
    if (context?.styleInsights && Object.keys(context.styleInsights).length > 0) {
      const styles = Object.entries(context.styleInsights)
        .map(([idx, desc]) => `Style ${parseInt(idx)+1}: ${desc}`)
        .join(', ');
      notes.push(`\nSTYLE CONTEXT: ${styles}`);
    }
  }

  // Return backward-compatible shape
  const combined = notes.join('');
  return { visionNote: combined, styleNote: '' };
}

/**
 * Build a human-readable summary of tagged images for the system prompt.
 * e.g. "2 subject image(s), 1 style image(s), and 1 scene image(s)"
 */
function buildImageSummary(context) {
  const parts = [];
  if (context?.imagesByRole) {
    for (const [role, ids] of Object.entries(context.imagesByRole)) {
      if (ids?.length > 0) parts.push(`${ids.length} ${role} image(s)`);
    }
  }
  if (parts.length > 0) return parts.join(', ');
  // Fallback to legacy counts
  const legacy = [];
  if (context?.numReferences > 0) legacy.push(`${context.numReferences} reference image(s)`);
  if (context?.numStyleImages > 0) legacy.push(`${context.numStyleImages} style image(s)`);
  return legacy.join(' and ') || 'tagged images';
}

/**
 * Build subtractive enhancement rules based on which roles are "claimed" by images.
 * Each tagged role tells the enhancer what NOT to add (the image handles that dimension).
 */
function buildSuppressionRules(context) {
  const rules = [];
  const roles = context?.imagesByRole || {};

  const hasSubject = (roles.subject?.length || 0) > 0;
  const hasStyle = (roles.style?.length || 0) > 0;
  const hasScene = (roles.scene?.length || 0) > 0;
  const hasStructure = (roles.structure?.length || 0) > 0;

  // What to SUPPRESS (image handles this dimension)
  if (hasSubject) rules.push('- DO NOT describe what the subject looks like — the Subject image provides this');
  if (hasStyle) rules.push('- DO NOT add artistic style, color palette, or lighting mood terms — the Style image provides this');
  if (hasScene) rules.push('- DO NOT add environment or setting descriptions — the Scene image provides this');
  if (hasStructure) rules.push('- DO NOT add composition, layout, or pose terms (centered, rule of thirds, wide shot) — the Structure image provides this');

  // What to ENHANCE (fill the gaps)
  const enhance = [];
  if (!hasSubject) enhance.push('subject details');
  if (!hasStyle) enhance.push('artistic style and lighting');
  if (!hasScene) enhance.push('environment and atmosphere');
  if (!hasStructure) enhance.push('composition and framing');
  enhance.push('action, mood, and technical quality');

  if (enhance.length > 0) {
    rules.push(`- FOCUS enhancement on: ${enhance.join(', ')}`);
  }

  return rules.join('\n');
}

/**
 * Prompt Enhancement System Prompts
 * 
 * MANUAL EDITING: You can edit these system prompts directly without touching component code.
 * Just modify the systemPrompt string and save this file.
 */
/**
 * A single-clip video enhancement, built on the shared shot contract.
 *
 * The eight per-model single-clip configs were eight restatements of what a shot is, each
 * with its own ordering, its own labels for the same context, and its own reply shape. That
 * layering is inverted: it is why the storyboard could be fixed -- cast deletion, unpickable
 * camera values, internal handles leaking into prose -- while these eight kept the old
 * behaviour, invisibly, for as long as nobody ran a live call against them.
 *
 * James, 2026-08-23: "I do want to keep the ability to customize the enhance per model, but
 * that should be downstream of the source of truth." So a model contributes exactly three
 * things -- a headline, a guidance paragraph about how that engine likes to be written for,
 * and a temperature -- and they sit AFTER the contract body, where they cannot displace the
 * user's line the way `GLOBAL STYLE DEFAULT` once did.
 *
 * A single clip asks for exactly what its two lanes can STORE: `framing`, `cameraMovement`
 * and `lens` on `videoParams.camera`, plus `blocking` and `device` on
 * `videoParams.direction` (`singleClipCamera.js`). Acting notes are still excluded -- they
 * need a per-character identity a single clip only has as loose @mentions -- and anything
 * else would come back looking like a filled chip and vanish on the next write.
 */
const SINGLE_CLIP_SLOTS = ['framing', 'cameraMovement', 'lens'];

/**
 * The sequence shot asks for LESS, and it is a separate constant for that reason.
 *
 * These two lists were briefly the same one. Widening it in place for the single clip
 * would have silently changed the `video-sequence-shot` prompt as well -- a surface whose
 * apply path writes only framing and movement, so it would have started asking for a lens
 * with nowhere to put it, which is the ask-only-what-you-store rule broken in the other
 * direction. Extending the sequence shot is real work (its manifest CAN store all three)
 * with its own apply sites and its own live matrix, and it is not this change.
 */
const SEQUENCE_SHOT_SLOTS = ['framing', 'cameraMovement'];

function buildSingleClipShotConfig({ headline, guidance, temperature, maxTokens }) {
  return {
    // Read by `enhancePromptWithConfig`, which unwraps the reply to its prose so that every
    // caller expecting a string keeps getting one, and by `enhanceShotWithConfig`, which
    // returns the fields as well. Without this flag the parse would have to be repeated at
    // each of the four call sites, which is how one of them gets missed and a user watches
    // raw JSON land in the prompt box.
    structuredShotReply: true,
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);
      const referenceNote = visionNote
        ? `${visionNote}\nTreat the attached image context as continuity anchors. Preserve them unless the user asks otherwise.`
        : '';
      const sourceNote = context?.hasSourceImage
        ? '\nThe user has a source image. It is the opening frame: describe what MOVES and what changes from there, not what it already shows.'
        : '';
      const castTokens = Array.isArray(context?.castTokens) ? context.castTokens : [];

      return `${headline}${sourceNote}

${buildShotContractBody({
        referenceNote,
        castTokens,
        elementContext: context?.elementContext || null,
        genreDirective: context?.genreDirective || null,
        slotNames: SINGLE_CLIP_SLOTS,
        // Nowhere to store one. See the contract's cast block.
        withPerformances: false,
      })}${buildChosenMovementNote(context?.cameraDirective)}${buildMotionNote(context?.speedSuffix)}

${guidance}${buildReplyShape({
        slotNames: SINGLE_CLIP_SLOTS,
        proseFields: SHOT_CONTRACT_PROSE_FIELDS,
        withBlockingVariants: true,
      })}${buildShotContractRules({
        slotNames: SINGLE_CLIP_SLOTS,
        proseFields: SHOT_CONTRACT_PROSE_FIELDS,
        unit: 'shot',
        withBlockingVariants: true,
      })}`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature,
    // Each model's own budget plus room for the JSON envelope. A truncated reply parses as
    // prose and silently loses every field, which looks exactly like a model that ignored
    // the shape -- so the headroom is deliberate rather than generous.
    //
    // +120 originally; +320 for the four BLOCKING variants; +640 once the four DEVICE
    // variants joined them, because the reply now carries the prose, five facts and EIGHT
    // generated sentences -- roughly 400 tokens of content.
    //
    // ⚠️ This is headroom for the ASK, and NOT the fix for the shape errors, though I first
    // read it that way. The refused replies were 99 characters, cut mid-word, against a
    // 1040-token budget -- 2.5x what the reply needs, so the limit was never the binding
    // constraint. The provider was stopping early and saying so in a `partial` flag nothing
    // read: see `PromptEnhancementPartialError` in `openRouterService.js`.
    maxTokens: maxTokens + 640,
  };
}

export const PROMPT_ENHANCEMENT_CONFIG = {
  // ===== GENERATE MODULES =====

  // Local model, runs on the user's own machine via GlowGhost Engine.
  // The no-login default.
  'sdxl-local': {
    systemPrompt: () => `Enhance this prompt for Stable Diffusion XL running locally.

SDXL responds to comma-separated descriptive phrases rather than sentences, and it is guidance-driven, so clear subject-first phrasing matters more than length.

ENHANCE WITH:
- The main subject first, then setting, then lighting and mood
- Concrete visual detail: materials, textures, time of day, specific colors
- Composition and framing, plus medium, lens, or artistic style when it matters
- Comma-separated phrases, strongest terms earliest

AVOID:
- Command phrasing ("create an image of...", "generate...") — describe the image
- Very long prompts: SDXL's text encoder truncates, so front-load what matters
- Prompt weighting syntax or embeddings the local install may not have

Return only the enhanced prompt.`,
  },

  'flux1-schnell-local': {
    systemPrompt: () => `Enhance this prompt for FLUX.1 schnell — a fast, distilled image model that renders in very few steps.

FLUX follows natural language closely and does not need keyword stuffing. Because schnell is distilled for speed, it rewards a clear, well-ordered description and does not benefit from long tails of quality adjectives.

ENHANCE WITH:
- The main subject first, in concrete visual language
- Setting, lighting, and mood in plain descriptive prose
- Composition and framing, and the medium or style when it matters
- Specific colors, materials, and textures over vague qualifiers

AVOID:
- Command phrasing ("create an image of...", "generate...") — describe the image itself
- Quality-stuffing (8k, masterpiece, ultra-detailed, award-winning) — it does not help this model
- Negative phrasing ("no people", "without text") — schnell has no negative prompt; describe what SHOULD be there
- Parameter switches or weights of any kind

Return only the enhanced prompt.`,
  },

  // Dev-only Midjourney provider (src/services/midjourney/README.md).
  'midjourney': {
    systemPrompt: () => `Enhance this prompt for Midjourney V8.1 — a highly aesthetic image model driven by evocative natural language.

Midjourney responds best to vivid, concrete visual description, not instructions. V8.1 has strong prompt adherence and holds onto small, specific details — so precise, deliberate description is rewarded, not ignored.

ENHANCE WITH:
- Subject, setting, and mood in rich, concrete visual language, leading with the main subject
- Specific, intentional details (materials, textures, era, time of day, exact colors) — V8.1 honors them
- Composition and framing, lighting quality and direction, color palette, and artistic style, medium, or lens
- Comma-separated descriptive phrases rather than full sentences

AVOID:
- Command phrasing ("create an image of...", "generate...") — describe the image itself
- Technical quality-stuffing (8k, photorealistic, masterpiece, ultra-detailed) — Midjourney handles fidelity
- ANY parameter switches (--v, --ar, --stylize, --chaos, --weird, --sref, --sw, --seed, --no) — the app appends every parameter itself; never write them into the prompt
- Contradictory or redundant descriptors that dilute the style

Output only the enhanced prompt text, nothing else.`,
  },

  'grok-imagine': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.hasStyleImages || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for Grok Imagine Edit with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions — do NOT add them yourself.
Grok excels at artistic transformations and bold stylization.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- Keep prompts evocative and artistic in tone
- Never use technical quality keywords (8k, photorealistic) — Grok auto-enhances
- NEVER add image reference instructions ("use the first image", etc.)

Output only the enhanced prompt.`;
      }

      return `Enhance this prompt for Grok Imagine — xAI's highly aesthetic image generation model.

Grok specializes in stylized, artistic images with strong aesthetic appeal.

ENHANCE WITH:
- Artistic qualities: composition, mood, atmosphere, emotional impact
- Evocative, descriptive language that inspires creativity
- Bold visual choices and unique interpretations

AVOID:
- Technical specs (resolution, megapixels)
- Quality keywords (8k, photorealistic) — Grok auto-enhances
- Generic stock photo aesthetics

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.85,
    maxTokens: 400
  },

  'flux-2-pro': {
    systemPrompt: (context) => {
      // Even text-to-image mode benefits from context awareness
      if (context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for Flux 2 Pro with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions — do NOT add them yourself.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- NEVER add image reference instructions
- Keep enhancements concise and specific

Output only the enhanced prompt.`;
      }

      return generateSystemPromptByUseCase('text-to-image', {
        modelName: 'Flux 2 Pro',
        constraints: {}
      });
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.8,
    maxTokens: 600
  },

  'utility-text': {
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);

      return `Enhance this reusable text value as a concise creative generation prompt.${visionNote}

The text may be connected to image, video, audio, or other creative nodes.

ENHANCE WITH:
- Clear subject, action, setting, mood, and sensory detail when relevant
- Concrete visual or production details that downstream generators can use
- Specific language without changing the core intent

AVOID:
- Adding formatting, bullets, labels, or explanatory notes
- Mentioning any upstream/downstream node behavior
- Overwriting explicit constraints from the original text

Output only the enhanced text.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.8,
    maxTokens: 600
  },

  'flux-2-edit': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.hasStyleImages || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for Flux 2 Edit with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions — do NOT add them yourself.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- NEVER add image reference instructions ("use the first image", "@image1")
- Keep enhancements concise

Output only the enhanced prompt.`;
      }

      return generateSystemPromptByUseCase('image-to-image', {
        modelName: 'Flux 2 Edit',
        constraints: {}
      });
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 300
  },

  'seedream-4.5': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.hasStyleImages || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for Seedream 4.5 with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions where needed — do NOT add special syntax yourself.
Seedream 4.5 is strong at photorealistic output, composition, and reference-driven transformations.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- Be explicit about the target visual outcome
- Say what should remain consistent when editing from references
- Keep the language concrete and production-oriented

Output only the enhanced prompt.`;
      }

      return `Enhance this prompt for Seedream 4.5, a fast high-quality image model suited to photorealistic scenes, compositions, and reference-driven generation.

ENHANCE WITH:
- Clear subject, environment, action, and framing
- Concrete text/layout instructions when readable typography matters
- Specific continuity constraints when the image should preserve an existing composition or object

AVOID:
- API syntax or image reference tokens
- Generic quality filler
- Contradictory stylistic directions

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 400
  },

  'krea-2-large': {
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);
      const styleNote = (context?.hasStyleImages || context?.totalImages > 0)
        ? `${visionNote}\nThe attached images set the LOOK, not the content. Do not describe them; describe the subject and let the references carry the aesthetic.`
        : '';

      return `Enhance this prompt for Krea 2 Large, a foundation image model built for aesthetic control rather than safe, polished output.${styleNote}

Krea rewards prompts written like a photographer or illustrator describing a real image: medium, light, lens, texture, and mood. It will happily render raw, expressive, or unconventional looks when you ask for them.

ENHANCE WITH:
- The subject and what it is doing, concretely
- Medium and process: film stock, print, painting, render, camera and lens
- Light: direction, quality, colour, time of day
- Texture and imperfection when they belong: grain, bloom, motion blur, dust
- Composition and framing

AVOID:
- Generic quality filler like "masterpiece", "8k", "highly detailed"
- Stacking contradictory styles
- API syntax or reference tokens

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.75,
    maxTokens: 380
  },

  'krea-2-medium': {
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);
      const styleNote = (context?.hasStyleImages || context?.totalImages > 0)
        ? `${visionNote}\nThe attached images set the LOOK, not the content. Describe the subject; let the references carry the style.`
        : '';

      return `Enhance this prompt for Krea 2 Medium, a fast Krea model post-trained for illustration, anime, painting, and expressive styles.${styleNote}

It is strongest on drawn and painted looks rather than photoreal ones, so name the illustrative idiom you want.

ENHANCE WITH:
- The subject, its action, and its mood
- The drawn idiom: line weight, brushwork, cel shading, ink, gouache, screen tone
- Palette and lighting in plain terms
- Composition and framing

AVOID:
- Generic quality filler
- Mixing photoreal camera language into an illustrated look
- API syntax or reference tokens

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.75,
    maxTokens: 340
  },

  'krea-2-turbo': {
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);
      const styleNote = (context?.hasStyleImages || context?.totalImages > 0)
        ? `${visionNote}\nThe attached images are look references. Describe the subject, not the references.`
        : '';

      return `Enhance this prompt for Krea 2 Turbo, the speed-optimized Krea model used for rapid ideation.${styleNote}

Turbo runs in seconds and is used to explore, so keep the prompt tight. One clear image idea beats a long production note.

ENHANCE WITH:
- Subject, setting, and action in one confident sentence or two
- One decisive style or medium cue
- Light and framing, briefly

AVOID:
- Long paragraphs and stacked adjectives
- Generic quality filler
- API syntax or reference tokens

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.72,
    maxTokens: 260
  },

  'seedream-5-pro': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.hasStyleImages || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for Seedream 5.0 Pro with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions where needed — do NOT add API-specific syntax yourself.
Seedream is strong at layout reasoning, typography, and coherent multi-image edits.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- Be explicit about what should change versus what must stay consistent
- Keep the prompt concrete and readable rather than overly ornate
- Prefer plain natural-language instructions over special tokens or shorthand

Output only the enhanced prompt.`;
      }

      return `Enhance this prompt for Seedream 5.0 Pro, a reasoning-focused image model that is especially strong at composition, typography, and precise instructions.

ENHANCE WITH:
- Clear subject, action, setting, and composition
- Concrete text/layout instructions when the image should contain readable text
- Specific preservation constraints when consistency matters

AVOID:
- API syntax or image reference tokens
- Vague quality fluff ("masterpiece", "best quality")
- Overwriting the user's intended composition with unrelated embellishment

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.65,
    maxTokens: 400
  },

  'gpt-image-2.5': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.hasStyleImages || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for GPT Image 2.5 Flare with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions where needed — do NOT add API-specific syntax yourself.
GPT Image 2.5 Flare is strong at instruction following, legible typography, structured layouts, and coherent boards.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- Make layout, hierarchy, and text requirements explicit when the user asks for boards, sheets, infographics, or sequences
- Preserve identity, style, and composition constraints from references without restating what the images already provide
- Keep the prompt concrete and production-ready

Output only the enhanced prompt.`;
      }

      return `Enhance this prompt for GPT Image 2.5 Flare, OpenAI's high-fidelity image generation and editing model.

ENHANCE WITH:
- Precise subject, setting, composition, and art direction
- Clear layout and panel instructions when the request involves a brand board, mood board, shotlist, character sheet, lookbook, or infographic
- Exact quoted text only when the user wants readable text in the image
- Concrete visual hierarchy and separation between panels for board-style outputs

AVOID:
- API syntax or image reference tokens
- Generic quality filler ("8k", "masterpiece", "ultra detailed")
- Inventing labels or text the user did not ask for

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.65,
    maxTokens: 450
  },

  'gpt-image-2.5-sunburst': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.hasStyleImages || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for GPT Image 2.5 Sunburst with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions where needed — do NOT add API-specific syntax yourself.
GPT Image 2.5 Sunburst is optimized for premium visual work, intricate detail, precise editing, legible typography, and coherent boards.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- Specify fine material, texture, typography, and layout details that must survive close inspection
- Preserve identity, style, and composition constraints from references without restating what the images already provide
- Keep edits tightly scoped and make preservation requirements explicit

Output only the enhanced prompt.`;
      }

      return `Enhance this prompt for GPT Image 2.5 Sunburst, OpenAI's precision-focused image generation and editing model.

ENHANCE WITH:
- Precise subject, setting, composition, material, and art direction
- Fine detail and hierarchy that should survive close inspection
- Exact quoted text only when the user wants readable text in the image
- Explicit preservation constraints for tightly scoped edits

AVOID:
- API syntax or image reference tokens
- Generic quality filler ("8k", "masterpiece", "ultra detailed")
- Inventing labels or text the user did not ask for

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.6,
    maxTokens: 450
  },
  
  'nano-banana-2': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.hasStyleImages || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        const imageSummary = buildImageSummary(context);
        const suppress = buildSuppressionRules(context);

        return `Enhance this prompt for Nano Banana 2 with ${imageSummary}.${visionNote}

The system will automatically inject image reference instructions — do NOT add them yourself.

ENHANCEMENT STRATEGY — fill in the GAPS, not what images already provide:
${suppress}
- NEVER add image reference instructions
- Keep it concise — Nano is powerful and doesn't need verbose prompts

Output only the enhanced prompt.`;
      }

      return generateSystemPromptByUseCase('image-to-image', {
        modelName: 'Nano Banana 2',
        constraints: {}
      });
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 300
  },
  
  'add-object': {
    systemPrompt: `You are an expert at crafting prompts for multi-reference image-to-image AI models (Flux 2 Edit and Nano Banana 2).

Your task: Enhance prompts for adding objects to existing scenes while maintaining scene coherence.

The user has selected:
- A scene image (the base environment)
- One or more object images (items to add to the scene)

Focus on these critical aspects:
- Precise placement and spatial relationships ("on the table", "next to the chair", "in the foreground")
- Lighting and shadow consistency ("matching the warm ambient lighting", "casting realistic shadows")
- Scale and perspective matching ("at appropriate size for the distance", "maintaining perspective")
- Natural integration ("blending seamlessly", "as if it were originally there")
- Material and texture coherence ("matching the scene's realism", "consistent surface reflections")

Keep instructions conversational and direct:
✓ "Place the red lamp on the left side of the wooden table, matching the warm ambient lighting and casting realistic shadows"
✓ "Add the modern chair in the foreground, scaled appropriately and matching the room's perspective"
✓ "Position the vase on the shelf at eye level, maintaining consistent lighting with the scene"

✗ Avoid:
- Overly technical language
- Multiple unrelated additions at once
- Quality keywords ("photorealistic", "8k", "ultra-detailed") - models handle this automatically
- Describing the objects themselves (they're in the reference images)

Output only the enhanced prompt, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 400
  },

  // ===== EDIT MODULES =====

  'imageAttributeSlider': {
    systemPrompt: `Rewrite the user's request as one concise preservation-first image characteristic adjustment.

The output will be used to build an image-to-video semantic slider from the existing image. Preserve the user's exact intent, keep the current image structure locked, and avoid adding unrelated edits.

Good outputs:
- "Increase only the moss coverage on the existing stonework while preserving the exact framing, architecture, lighting, background, and camera."
- "Adjust only the person's hair length from short to longer while preserving identity, pose, clothing, lighting, background, camera, and style."
- "Vary only the saturation of the red jacket while preserving all other colors, objects, composition, and image details."

Rules:
- Name exactly one characteristic to vary.
- State that the change applies only to existing visible target regions.
- Include what must stay unchanged: composition, framing, perspective, object layout, lighting, background, camera, and style.
- Do not add quality keywords, camera moves, new objects, new architecture, new scenery, or new scene details.
- Output only the enhanced instruction.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.45,
    maxTokens: 220
  },

  'imageCameraAngleExplorer': {
    systemPrompt: `Rewrite the user's camera-angle intent as one concise direction for exploring an H3 camera path.

The output will be combined with a deterministic target camera pose, then the user will choose a frame from the generated path. Preserve the original subject, scene identity, lighting logic, style, and object relationships while allowing only a plausible camera perspective change.

Good outputs:
- "Find a stronger low hero angle around the existing product while preserving the same product identity, set, lighting, materials, and color palette."
- "Explore a subtle orbit to the right around the existing character while preserving the character identity, pose intent, wardrobe, background, lighting, and style."
- "Push in slightly toward the existing castle entrance while preserving the architecture, weather, scene layout, lighting, and gothic style."

Rules:
- Name one camera intent or focal priority.
- Keep the current scene and subject identity locked.
- Allow only plausible perspective reconstruction across newly revealed edges.
- Do not request new objects, new scenery, style changes, text, logos, or arbitrary composition replacement.
- Output only the enhanced instruction.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.45,
    maxTokens: 220
  },

  'imageRelightExplorer': {
    systemPrompt: `Rewrite the user's scene-lock note as one concise preservation description for image relighting.

The output will be combined with a deterministic light-direction instruction. Preserve the original scene, subject identity, materials, object layout, camera angle, background, and style. Do not introduce new lighting directions; those come from the relight widget.

Good outputs:
- "Same glass perfume bottle on the blue silk backdrop, preserving the label, reflections, fabric folds, and front-facing product framing."
- "Same person in the existing leather jacket against the brick wall, preserving pose, face identity, wardrobe, background details, and camera angle."
- "Same kitchen counter product setup, preserving all props, packaging text, surface texture, composition, and camera perspective."

Rules:
- Describe only what should stay locked.
- Do not request new objects, new scenery, new composition, camera movement, style changes, logos, or text edits.
- Do not name a light direction, color temperature, or lighting style.
- Output only the enhanced scene-lock phrase.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.35,
    maxTokens: 180
  },
  
  // Generative Fill - Smart Edit (instruction-based, no mask)
  'generativeFill:smart': {
    systemPrompt: `You are an expert at creating natural editing instructions for Qwen Image Edit.

Transform the user's idea into a clear, conversational instruction.

The model excels at:
- Style transformations ("make it look like watercolor painting")
- Lighting adjustments ("change to golden hour lighting")
- Object modifications ("add sunglasses to the person")
- Background changes ("make the background blurry")
- Color grading ("convert to black and white")
- Atmosphere ("add fog", "make it rain")

Keep instructions simple and direct:
✓ "Make the sky more dramatic"
✓ "Add a red jacket to the person"
✓ "Change lighting to sunset"
✓ "Make background bokeh effect"
✓ "Add autumn colors to the trees"

✗ Avoid:
- Overly detailed descriptions
- Specifying exact pixel locations
- Multiple unrelated changes at once
- Quality keywords like "8k", "photorealistic"

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 200
  },
  
  // Generative Fill - Precise Mode (mask-based inpainting)
  'generativeFill:precise': {
    systemPrompt: `You are an expert at creating targeted instructions for mask-based image inpainting.

The user has masked a specific area. Describe what should fill that area.

Best practices:
- Use direct verbs: "Remove", "Replace with", "Change to", "Add"
- Focus on the object/content, not location (mask defines location)
- Keep it concise and actionable

✓ Excellent examples:
- "Remove completely"
- "Replace with red dress"
- "Change to wooden door"
- "Add forest background"
- "Remove the person"
- "Replace with blue sky"

✗ Avoid:
- Location details (unnecessary, mask defines location)
- Overly descriptive prose
- Multiple instructions
- Quality terms

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 200
  },

  'generativeFill:precise:zImageTurbo': {
    systemPrompt: `You are an expert at creating concise prompts for fast mask-based inpainting.

The user has painted a mask. Describe only what should appear inside the masked area.

Best practices:
- Keep the prompt short and concrete
- Focus on the replacement content, material, color, and texture
- Prefer simple object or scene descriptions over multi-step instructions

Output only the refined prompt, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.65,
    maxTokens: 120
  },

  'generativeFill:precise:fluxFill': {
    systemPrompt: `You are an expert at creating prompts for FLUX.1 Pro Fill mask-based inpainting.

The user has masked a specific region. Describe what should fill the masked region while preserving the surrounding image.

Best practices:
- State the desired replacement directly
- Include lighting, material, and texture when they matter
- Keep preservation instructions minimal because the mask defines the edit area

Output only the refined prompt, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.68,
    maxTokens: 180
  },

  'generativeFill:precise:gptImage25': {
    systemPrompt: `You are an expert at creating high-fidelity masked edit instructions for GPT Image 2.5 Flare.

The user has painted a mask. Write a clear natural-language instruction for what should change inside the mask and what nearby visual qualities should be preserved.

Best practices:
- Describe the replacement content and how it should integrate with the scene
- Mention lighting, perspective, edges, and texture only when useful
- Keep the instruction specific and non-ornate

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.68,
    maxTokens: 220
  },

  'expand-canvas-bria': {
    systemPrompt: generateSystemPromptByUseCase('outpainting', {
      modelName: 'Bria Expand',
      constraints: {
        language: 'English',
        noSpecialChars: true,
        maxLength: 200
      }
    }),
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.6, // Lower temp for more controlled output
    maxTokens: 300
  },

  'smart-resize': {
    systemPrompt: `You are an expert at writing concise composition guidance for Smart Resize.

The source image already exists. The model will adapt it to exact target dimensions while preserving the original subject and intent.

Enhance the user's guidance into a direct instruction for how the composition should adapt.

Focus on:
- What should stay visually important after resizing
- How the subject, text, product, or focal point should be framed
- Any background extension or cropping preference

Avoid:
- Rewriting the whole image prompt
- Adding unrelated new objects, styles, or quality keywords
- Mentioning pixel dimensions or API details

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.55,
    maxTokens: 220
  },

  'removeObject:smart': {
    systemPrompt: `You are an AI assistant specialized in refining object removal instructions for the Object Remover model.

The model removes unwanted elements from images based on natural language descriptions. Your role is to enhance user prompts while keeping them focused and actionable.

Model capabilities:
- Removes objects, people, text, logos, watermarks
- Maintains image consistency and quality
- Automatically fills removed areas naturally
- Preserves original style and composition

Input: User's removal instruction (may be vague or detailed)
Output: Clear, concise removal instruction

Best practices:
- Use direct imperative verbs: "Remove", "Delete", "Erase"
- Be specific about WHAT to remove: "Remove the person in red shirt", "Remove all text"
- Keep it concise (under 15 words if possible)
- Focus on the target, not the result

✓ Excellent examples:
- "Remove the person on the left"
- "Remove all text and watermarks"
- "Delete the power lines from the sky"
- "Remove the red car"
- "Erase the date stamp"

✗ Avoid:
- Describing what should remain ("keep the background")
- Quality directives ("high quality", "seamless")
- Multiple instructions ("remove X and also Y and change Z")
- Vague terms ("remove the thing")

Output only the refined removal instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.6,  // Lower temp for precise removal instructions
    maxTokens: 100     // Keep it short
  },

  // NOTE: No 'removeObject:precise' config - that mode doesn't use prompts!

  // The graph Mask node's text/object selection request (SAM 3 text segmentation). Only run when
  // the user presses Enhance; a recorded request is never rewritten.
  'mask': {
    systemPrompt: `You refine a short description of what to SELECT in an image, for a text-prompted segmentation model (SAM 3).

The model finds every region matching a noun phrase. It does not edit, generate or restyle anything.

Rules:
- Keep the exact target the user named. Never swap it for a different object, widen it to a category, or add a second target.
- Output a short noun phrase (2-8 words), e.g. "the red car", "people in the foreground", "the wooden table".
- You may add one distinguishing visual attribute only if the user already implied it (color, position, size).
- No verbs, no instructions ("select", "mask", "remove"), no quality words, no style, lighting or mood.

Output only the noun phrase, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.3,
    maxTokens: 40
  },

  // ===== VIDEO MODULES =====

  'video-kling': buildSingleClipShotConfig({
    headline: 'Enhance this prompt for Kling video generation (text-to-video / image-to-video).',
    guidance: `KLING GUIDANCE -- how this engine likes a shot written:
- Order the prose: subject and key visual details, then action and motion, then context, then the camera move, then lighting and mood.
- Always give the motion an ENDPOINT -- "...then settles into stillness", "...before the camera holds". Open-ended motion causes generation failures.
- Keep the prose under 50 words. Kling degrades with verbose prompts.
- Sequential motion for multiple subjects; do not describe simultaneous actions.
- Do not mix conflicting lighting terms or stack simultaneous camera moves.`,
    temperature: 0.8,
    maxTokens: 400,
  }),

  'video-sequence-brief': {
    systemPrompt: (context) => {
      const isI2V = context?.hasSourceImage;
      const { visionNote } = buildContextNotes(context);
      const i2vNote = isI2V
        ? '\nThe user has a source image. Do NOT redescribe the exact source-frame appearance. Focus on continuity, motion, progression, and atmosphere.'
        : '';
      const referenceNote = visionNote
        ? `${visionNote}\nTreat the attached image context as continuity guidance for the full sequence arc. Preserve those anchors unless the user explicitly asks for a change.`
        : '';
      const cameraNote = context?.cameraDirective
        ? `\nGLOBAL CAMERA DEFAULT: ${context.cameraDirective}`
        : '';
      const genreNote = context?.genreDirective
        ? `\nGLOBAL STYLE DEFAULT: ${context.genreDirective}`
        : '';
      const speedNote = context?.speedSuffix
        ? `\nGLOBAL MOTION DEFAULT: ${context.speedSuffix}`
        : '';
      const elementNote = context?.elementContext
        ? `\nSCENE ASSET CONTEXT: ${context.elementContext}`
        : '';

      return `Enhance this sequence brief for multi-shot video generation.${i2vNote}${referenceNote}${cameraNote}${genreNote}${speedNote}${elementNote}

Goals:
- Return one cohesive sequence brief, not a shot list.
- Strengthen narrative progression, continuity, and visual arc.
- Keep the brief concise and production-oriented.
- Do not number shots or describe editing instructions.

Output only the enhanced sequence brief.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 350,
  },
  'video-kling-sequence-brief': {
    systemPrompt: (context) => PROMPT_ENHANCEMENT_CONFIG['video-sequence-brief'].systemPrompt(context),
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 350,
  },
  /**
   * One shot inside a sequence.
   *
   * Before this existed, a shot was enhanced with the SINGLE-CLIP config: it saw
   * the shot's own text, the global camera/style defaults and the scene assets,
   * and nothing else. Not the brief, not the neighbouring shots, not even which
   * number it was. So every shot came back as a self-contained little film --
   * each one opening and resolving -- which is exactly why a sequence had to be
   * written in ChatGPT and pasted in.
   *
   * The fix is the input, not the wording: this config exists to receive the
   * surrounding shots and refuse to restart the scene.
   */
  /**
   * ONE shot inside a longer sequence.
   *
   * It carried the same framing that deleted the cast from the storyboard -- `GLOBAL STYLE
   * DEFAULT` and `SCENE ASSET CONTEXT` as headline material, both ahead of the user's line --
   * and it had no cast list at all, so nothing ever told it that the people named in the
   * sentence had to be in the picture. It had also never been measured against a real model.
   *
   * It now composes the shared contract like every other shot surface, and contributes only
   * what is genuinely sequence-specific: where the shot sits, what is on either side of it,
   * and the continuity rules that follow from having neighbours.
   *
   * It asks for `framing` and `cameraMovement` and no more, because those are the two its
   * write path stores. The manifest does have a `lens` slot and the storyboard fills one, so
   * lens/blocking/device/acting-note parity here is a real gap -- but asking for a field this
   * path does not write would hand back a chip that vanishes, which is worse than the gap.
   */
  'video-sequence-shot': {
    structuredShotReply: true,
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);
      const i2vNote = context?.hasSourceImage
        ? '\nThe sequence has a source image. Do NOT redescribe the exact source-frame appearance; carry motion and continuity forward.'
        : '';
      const referenceNote = visionNote
        ? `${visionNote}\nTreat the attached image context as continuity anchors. Preserve them unless the user asks otherwise.`
        : '';
      const castTokens = Array.isArray(context?.castTokens) ? context.castTokens : [];

      // Where the shot sits. Reference for the user's line, so it comes AFTER the contract
      // body -- as headline material it was one of the two blocks that displaced the shot.
      const position = Number.isFinite(context?.shotIndex) && Number.isFinite(context?.shotCount)
        ? `\nThis is shot ${context.shotIndex + 1} of ${context.shotCount}.`
        : '';
      const briefNote = context?.sequenceBrief ? `\nTHE SEQUENCE IS: ${context.sequenceBrief}` : '';
      const beforeNote = context?.previousShotPrompt ? '' : '\nThis is the opening shot.';
      const afterNote = context?.nextShotPrompt ? '' : '\nThis is the closing shot.';
      // The WHOLE list, not just the neighbours. Passing only the adjacent shots let a
      // subject's identity drain away over two hops: shot one named a bulldog, shot two said
      // "the dog", and shot three -- which saw only shot two -- made it a golden retriever.
      const listNote = Array.isArray(context?.allShotPrompts) && context.allShotPrompts.length
        ? `\n\nTHE FULL SEQUENCE AS IT STANDS:\n${context.allShotPrompts
          .map((entry) => `${entry.number}. ${entry.isCurrent ? '>> THIS SHOT >> ' : ''}${entry.prompt || '(empty)'}`)
          .join('\n')}`
        : '';
      const durationNote = Number.isFinite(context?.shotDuration)
        ? `\nThis shot runs ${context.shotDuration} seconds, so it holds roughly one beat of action.`
        : '';

      return `Enhance ONE shot inside a longer video sequence.${i2vNote}

${buildShotContractBody({
        referenceNote,
        castTokens,
        elementContext: context?.elementContext || null,
        genreDirective: context?.genreDirective || null,
        slotNames: SEQUENCE_SHOT_SLOTS,
        withPerformances: false,
      })}${buildChosenMovementNote(context?.cameraDirective)}${buildMotionNote(context?.speedSuffix)}${briefNote}${position}${beforeNote}${afterNote}${durationNote}${listNote}${buildReplyShape({
        slotNames: SEQUENCE_SHOT_SLOTS,
      })}${buildShotContractRules({
        slotNames: SEQUENCE_SHOT_SLOTS,
        unit: 'shot',
        extraRules: '\n- Enhance ONLY the shot marked ">> THIS SHOT >>". Do not rewrite the others.'
          + '\n- The SUBJECT MUST BE IDENTICAL to the subject in the other shots. If any shot'
          + '\n  names a breed, model, colour, wardrobe or age, use exactly that.'
          + '\n- Keep location, lighting and time of day consistent with the rest of the list.'
          + '\n- Continue the action rather than re-establishing the scene, unless this is the'
          + '\n  opening shot.'
          + '\n- Do not resolve the story unless this is the closing shot.'
          + '\n- Describe one clear beat of action, sized to the shot\'s duration.',
      })}`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    // 320 plus room for the JSON envelope. A truncated reply parses as prose and loses every
    // field, which is indistinguishable from a model that ignored the shape.
    maxTokens: 440,
  },

  /**
   * A single storyboard shot.
   *
   * Distinct from `video-sequence-shot` because a storyboard shot is a STILL by default and
   * has no neighbouring shots to stay continuous with -- passing it the sequence config asked
   * it not to re-establish a scene that nothing else had established.
   *
   * It returns the camera facts as FIELDS beside the prose so the card can write them onto the
   * shot, which is what makes the words in the sentence controls rather than text.
   *
   * `blocking` and `device` are requested for the same reason, and they are why an enhanced
   * shot now arrives with adjustable direction rather than prose the user would have to
   * rewrite: each one lands on the manifest and renders as a chip in the compiled sentence.
   * James, 2026-08-22: "The enhance's job is to come back with usable chips that I can then
   * adjust." Cast performance arrives here too, keyed by a request-scoped character token --
   * see the contract's acting-notes block.
   *
   * ⚠️ The contract's rules say each camera fact appears in the prose ONCE. They deliberately
   * do NOT yet ask for `{{framing}}` markers: nothing renders a marker until a later slice,
   * and an unrendered marker would reach the screen -- or a paid generation -- as literal
   * braces.
   */
  'storyboard-shot': {
    // Every block below comes from `promptEnhancementShotContract.js`, which owns what an
    // enhanced shot IS. This config now contributes only what is storyboard-specific: the
    // headline, the reference note, and which slots a storyboard shot can STORE.
    //
    // The order of those blocks is the fix, not decoration -- see the contract's header. The
    // assembled result is pinned byte for byte by `storyboardShotPromptGolden.test.js`, so a
    // reword here fails loudly rather than quietly changing what a paid call reads.
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);
      const referenceNote = visionNote
        ? `${visionNote}\nTreat the attached image context as continuity anchors. Preserve them unless the user asks otherwise.`
        : '';
      const castTokens = Array.isArray(context?.castTokens) ? context.castTokens : [];
      const slotNames = ['framing', 'cameraMovement', 'lens'];
      const proseFields = ['blocking', 'device'];

      return `Enhance ONE storyboard shot into a single vivid still image description.

${buildShotContractBody({
        referenceNote,
        castTokens,
        elementContext: context?.elementContext || null,
        genreDirective: context?.genreDirective || null,
        shotDuration: context?.shotDuration,
        slotNames,
        performanceBaselines: context?.performanceBaselines,
      })}${buildReplyShape({
        slotNames,
        proseFields,
        withPerformances: castTokens.length > 0,
      })}${buildShotContractRules({ slotNames, proseFields })}`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    // Raised from 320 with the acting notes. The reply now carries prose, six camera and
    // direction fields, AND one note per character; a truncated JSON reply parses as prose
    // and silently loses every field, which is indistinguishable from a model that ignored
    // the shape.
    maxTokens: 700,
  },

  'video-ltx': buildSingleClipShotConfig({
    headline: 'Enhance this prompt for LTX Video 2.5 -- a fast, lightweight video model with strong prompt comprehension.',
    guidance: `LTX GUIDANCE -- how this engine likes a shot written:
- One clear scene. No transitions, no multi-shot sequences.
- Use specific action verbs: "strides", "drifts", "cascades" -- not "walks", "moves", "falls".
- State the camera movement explicitly in the prose as well as in the field.
- Name the lighting and atmosphere: time of day, light quality, colour temperature.
- Keep it concise and direct. LTX responds best to unambiguous descriptions.`,
    temperature: 0.8,
    maxTokens: 400,
  }),

  'video-grok': buildSingleClipShotConfig({
    headline: 'Enhance this prompt for Grok Imagine Video 1.5, which generates its own audio.',
    // MEASURED 2026-08-24: the third config caught by the inert gate, at 1/2 -- lower than
    // seedance and gemini, which is why only a gate would find it. Same shape as both: every
    // line asked for restraint or continuity, and none asked it to ADD, so a user's finished
    // sentence already satisfied the brief. The one-to-two-sentence ceiling is a real Grok
    // preference and is kept; a tight sentence still has to say something new.
    guidance: `GROK GUIDANCE -- how this engine likes a shot written:
- ALWAYS add what the user's line leaves unsaid: the light, the surfaces, the sound, what the
  subject's body is doing. Handing their sentence back unchanged is not an enhancement.
- One cinematic beat, one to two production-ready sentences -- tight, not untouched.
- Name subject motion, environmental motion and camera motion.
- Include concise audio direction where it carries the moment: ambience, effects, music, dialogue.
- Keep identity and composition continuous with any source image.
- Concrete cinematic language, no quality filler.`,
    temperature: 0.75,
    maxTokens: 320,
  }),

  'video-restyle-lucy': {
    systemPrompt: (context) => {
      const sourceNote = context?.hasSourceVideo
        ? '\nThe user has a source video. Motion, timing, and scene content are already defined by the source.'
        : '';

      return `Enhance this prompt for Lucy Restyle video-to-video transformation.${sourceNote}

Lucy Restyle is best when the prompt is a focused description of the target look. The source video already provides motion, timing, composition, and scene content.

Your prompt should ONLY describe the desired visual treatment.

FOCUS ON:
- Specific art styles: "thick impasto oil paint", "cel-shaded anime", "1970s Kodachrome film stock"
- Textures and materials: "chrome reflections", "watercolor bleed", "charcoal sketch grain"
- Color palettes: "desaturated teal and amber", "neon-soaked cyberpunk palette"
- Lighting mood: "dramatic chiaroscuro", "soft diffused overcast", "harsh high-contrast noir"
- Rendering techniques: "3D wireframe overlay", "stipple halftone", "glitch datamosh"
- Continuity language like "preserve the source performance and blocking"

NEVER DESCRIBE:
- Motion, action, or movement (comes from source video)
- Scene content, subjects, or objects (comes from source video)
- Camera angles or framing (comes from source video)
- API flags or upload mechanics

Be concrete and specific. "Oil painting with visible impasto brushstrokes and warm amber highlights" not "artistic style".

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.85,
    maxTokens: 300,
  },

  'webcam-live-ai-lucy-2-5': {
    systemPrompt: () => `Enhance this instruction for Lucy 2.5 real-time video editing.

Lucy transforms a live camera feed continuously. It can restyle the scene, replace or remove backgrounds and objects, change attributes, add visual effects, and perform character or product transformations.

Write one concise, immediately actionable instruction:
- State exactly what should change
- Preserve the person, performance, framing, lighting, motion, and scene elements that are not being changed
- Use concrete visual language for style, materials, colors, lighting, or replacement details
- Avoid camera directions, duration, resolution, API language, and generic quality filler
- Do not invent additional edits beyond the user's request

Output only the enhanced instruction.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 220,
  },

  'video-restyle-kling-o1': {
    systemPrompt: (context) => {
      const sourceNote = context?.hasSourceVideo
        ? '\nA source video is already attached. Preserve its timing, framing, and action unless the user explicitly asks to change them.'
        : '';

      return `Enhance this prompt for Kling O3 video editing/restyling.${sourceNote}

Kling O3 responds best to concise, production-ready edit directions for existing footage.

FOCUS ON:
- The exact visual transformation that should be applied to the current clip
- Clear material, styling, lighting, wardrobe, or environment changes
- Continuity-preserving phrasing like "keep the original performance" or "retain the scene blocking"
- Short, direct wording that works with optional reference images

AVOID:
- Rewriting the full scene from scratch
- Long cinematic prose
- Extra camera direction or timing instructions unless requested
- Generic quality filler

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.72,
    maxTokens: 240,
  },

  'video-restyle-seedance': {
    systemPrompt: (context) => {
      const sourceNote = context?.hasSourceVideo
        ? '\nThe user has a source video. Treat it as the continuity anchor and preserve the original timing, motion, and framing.'
        : '';

      return `Enhance this prompt for Seedance 2.5 Draft reference-to-video restyling.${sourceNote}

This model edits or restyles existing footage using attached video and optional image references.

FOCUS ON:
- The exact visual treatment that should be applied to the source clip
- Specific materials, rendering style, lighting mood, and palette changes
- Preserving the source performance and scene continuity while changing the look
- Short, direct instructions that work well with attached reference media

AVOID:
- Rewriting the whole scene from scratch
- Adding camera directions or timing changes unless explicitly requested
- Generic quality filler
- Mentioning API mechanics, uploads, or token syntax

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.75,
    maxTokens: 260,
  },

  'video-extend': {
    systemPrompt: (context) => {
      const { visionNote } = buildContextNotes(context);
      // Extending the START prepends footage that must arrive AT the clip's
      // opening frame. Coaching "what happens next" there is backwards.
      const extendsStart = context?.side === 'start';
      const sourceNote = context?.hasSourceVideo
        ? (extendsStart
          ? '\nThe user has a source clip. The extension is prepended, so describe what leads UP TO its opening frame rather than redescribing what is already on screen.'
          : '\nThe user has a source clip. The extension continues from its final frames, so describe what happens NEXT rather than redescribing what is already on screen.')
        : '';
      const directionNote = extendsStart
        ? 'This extension plays BEFORE the existing clip and must land exactly on its first frame.'
        : 'This extension plays AFTER the existing clip and must start exactly from its last frame.';

      return `Enhance this prompt for extending an existing video clip.${sourceNote}${visionNote}

An extension is a continuation, not a new shot. ${directionNote} It inherits the clip's subject, wardrobe, location, lighting and camera position, and the join has to be invisible.

ENHANCEMENT STRATEGY:
- Say what CHANGES over the added seconds: the motion, the gesture, where the camera drifts
- Keep subject, wardrobe, location, palette and light continuous with the source unless the user asks otherwise
- Prefer one simple action over a new beat; an extension is short
- Do not describe a cut, a new angle, or a new scene
- Avoid quality filler like "masterpiece" or "cinematic 8k"

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 280
  },

  'video-edit-smart': {
    systemPrompt: (context) => {
      const sourceNote = context?.hasSourceVideo
        ? '\nThe user already has a source video. Keep the edit request focused on what should change in that existing clip.'
        : '';

      return `Enhance this prompt for prompt-guided video editing.${sourceNote}

This is a text-guided video edit request, not a fresh generation prompt.

FOCUS ON:
- The specific subject, object, wardrobe, prop, or environment change
- The exact visual outcome after the edit
- Localized, concrete instructions that can be applied to existing footage
- Continuity: preserve motion, framing, and overall scene coherence unless the user explicitly asks to change them

AVOID:
- Rewriting the whole scene from scratch
- Describing camera moves or timing unless they must change
- Generic quality filler like "high quality" or "cinematic"
- Long multi-step instructions

Aim for one concise, production-ready edit instruction. Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.65,
    maxTokens: 220,
  },

  'video-recast': {
    systemPrompt: (context) => {
      const count = Number(context?.performerCount) || 0;
      const castNote = count > 0
        ? `\nThe user attached ${count} performer photo${count === 1 ? '' : 's'}, numbered left to right in the order they will replace the main people.`
        : '';

      return `Enhance this optional casting direction for MiniMax H3 Recast, which replaces the main people in an existing clip with performer photos while keeping its motion, camera and soundtrack.${castNote}

FOCUS ON:
- Who replaces whom, referring to people by position (leftmost, second from left) or by performer number
- What must stay the same about each person: wardrobe, hairstyle, props, expression
- Anything the user explicitly wants changed on the new people

AVOID:
- Re-describing the scene, motion, camera or timing, which the source clip already fixes
- Describing faces in detail; the performer photos carry identity
- Generic quality filler like "cinematic" or "high quality"

Keep it under two short sentences. Output only the enhanced direction.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.5,
    maxTokens: 160,
  },

  'video-veo31': buildSingleClipShotConfig({
    headline: 'Enhance this prompt for Google Veo 3.1 -- a high-capability cinematic video model.',
    // MEASURED 2026-08-27: this config dropped the user's named subject in 5 of 8 runs on
    // "A dog named Rufus trots down a dark path" -- 63%, where the matrix at n=2 saw it once
    // and read as variance. The shared contract already says to keep every proper noun, but
    // every line below pulls toward atmosphere and mood, and none of them said to keep the
    // name, so the rule lost on weight -- the same shape as the inert-enhance defect, where
    // per-model guidance quietly overrode the shared ask. The engine's real preference for
    // rich cinematic description is kept; the missing half is now stated first.
    guidance: `VEO 3.1 GUIDANCE -- how this engine likes a shot written:
- KEEP THE SUBJECT'S NAME. If the user named someone -- "a dog named Rufus" -- that name is in
  your prose, and "the dog" instead of "Rufus" is a failed enhance however cinematic it reads.
- It thrives on rich, cinematic description and handles more detail than most. Three to four sentences is ideal.
- Use cinematic language: "rack focus", "shallow depth of field", "anamorphic lens flare".
- Give the camera angle AND the move a purpose in the prose.
- Name time of day and atmosphere: "pre-dawn blue hour", "harsh midday sun through venetian blinds".
- End on a mood reinforcer: "...evoking a sense of quiet solitude".
- POSITIVE descriptions only. Describe what is present, never what is absent.`,
    temperature: 0.8,
    maxTokens: 600,
  }),

  'video-gemini-omni': buildSingleClipShotConfig({
    headline: 'Enhance this prompt for Gemini Omni Flash video generation.',
    // MEASURED 2026-08-24, same defect as `video-seedance` and found in the same run: this
    // config handed a complete-sentence prompt straight back, unchanged. "Concise and direct"
    // and "no long cinematic filler" are both instructions to restrain, and nothing here
    // asked it to ADD -- so the model read the user's finished sentence as already correct.
    // Brevity is a real Gemini preference and is kept; the missing half is now stated.
    guidance: `GEMINI OMNI FLASH GUIDANCE -- how this engine likes a shot written:
- ALWAYS add what the user's line leaves unsaid: the light, the surfaces, what the subject's
  body is doing. Handing their sentence back unchanged is not an enhancement.
- Concise and direct with it. One coherent shot or transformation, tightly written -- short
  does not mean unchanged.
- Name subject action, camera movement and environmental motion as separate facts.
- Concrete visual details that preserve identity and continuity from any attached image.
- No long cinematic filler, no API syntax, no negative-prompt lists unless the user asked.`,
    temperature: 0.72,
    maxTokens: 320,
  }),

  'video-seedance': buildSingleClipShotConfig({
    // No version in the headline: three models share this config -- Seedance 2.5, 2.0, and
    // 2.0 Fast Reference -- and it said "2.0" while the default selection is 2.5.
    headline: 'Enhance this prompt for Seedance video generation.',
    // MEASURED 2026-08-24, 5/5 runs: this config returned the user's prompt BYTE-IDENTICAL,
    // so the enhance was inert and only the camera chips landed. LTX on the same prompt
    // added 517 characters. The old wording asked for "strong prompt adherence" and detail
    // "concrete rather than ornate" -- two instructions to leave the line alone, and no
    // instruction to add anything, so the model correctly concluded the input was the answer.
    //
    // Seedance's genuine preference is concrete over florid, and that is kept. What is added
    // is the thing an enhancement is FOR: name what the line leaves unsaid.
    guidance: `SEEDANCE GUIDANCE -- how this engine likes a shot written:
- ALWAYS add what the user's line leaves unsaid: the light, the surfaces, the weather, what
  the subject's body is doing. Returning their sentence back to them is not an enhancement.
- Concrete over florid. Name real things -- wet asphalt, sodium light, breath in cold air --
  rather than reaching for grand adjectives.
- State subject motion, environmental motion and camera motion clearly, as separate facts.
- One coherent shot or beat. Keep every proper noun and specific the user already wrote.
- Preserve continuity from attached media unless the user asked for a change.
- No filler like "masterpiece" or "best quality".`,
    temperature: 0.72,
    maxTokens: 320,
  }),

  'video-minimax-h3': buildSingleClipShotConfig({
    headline: 'Enhance this prompt for MiniMax H3, which renders picture and sound together in one pass.',
    guidance: `MINIMAX H3 GUIDANCE -- how this engine likes a shot written:
- It reads text, images, clips and audio in one context, so state the whole audio-visual intent rather than the picture alone.
- One continuous shot with a clear beginning and end.
- Name subject motion, environmental motion and camera motion separately.
- Say what the scene sounds like when sound matters: ambience, texture, presence.
- Keep continuity with any attached reference unless the user asked otherwise.
- Stay concrete; no "masterpiece", no "8k".`,
    temperature: 0.72,
    maxTokens: 340,
  }),

  'video-flux3': buildSingleClipShotConfig({
    headline: 'Enhance this prompt for FLUX 3 video generation.',
    guidance: `FLUX 3 GUIDANCE -- how this engine likes a shot written:
- It renders up to twenty seconds in one clip and can generate its own audio. Write for that length, not for a frozen moment.
- Give the shot a shape: how it opens, what changes, where it lands.
- Separate subject motion from camera motion, and pace both.
- Describe the sound of the scene when it carries the moment.
- Keep lighting, wardrobe and location consistent across the whole clip.
- Concrete and production-oriented; no quality filler.`,
    temperature: 0.72,
    maxTokens: 360,
  }),
  'generativeFill:smart:nanoBanana2Flash': {
    systemPrompt: `You are an expert at creating fast, natural editing instructions for Nano Banana 2 (Gemini 3.1 Flash Image).

This model is optimized for quick semantic image edits while preserving scene context. It performs best when the instruction is focused, visual, and easy to execute in one pass.

Strengths to leverage:
- Fast object, wardrobe, lighting, and style changes
- Context-aware preservation of surrounding scene details
- Direct natural-language edits without mask or coordinate syntax
- Short, coherent multi-part instructions when the changes are related

Write a clear editing instruction. Be specific about what should change and mention what should stay the same when preservation matters.

✓ Good: "Change the jacket to navy leather and keep the pose and lighting the same"
✓ Good: "Replace the cloudy sky with a warm sunset and preserve the city skyline"
✗ Avoid: Long production notes, API syntax, pixel coordinates, or unrelated requests

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.68,
    maxTokens: 220
  },

  // ─── Edit Mode: Nano Banana Pro Smart Edit ───
  'generativeFill:smart:nanoBanana2': {
    systemPrompt: `You are an expert at creating precise editing instructions for Nano Banana Pro (Gemini 3 Pro Image).

This model is the character-consistency option for semantic image editing. It trades speed for deeper reasoning, stronger identity preservation, and complex composition handling.

Strengths to leverage:
- Complex scene modifications with multiple related steps
- Character, product, and prop preservation across edits
- Text rendering in images ("add a sign that says 'Open'")
- Composition-aware changes that should respect lighting, reflections, and perspective
- Precise instructions about what must change and what must remain untouched

Write a clear, natural instruction with preservation constraints where useful. Be specific about what should change without over-specifying implementation details.

✓ Good: "Replace the sky with a dramatic sunset and add warm light reflections on the water"
✓ Good: "Change the person's outfit to a blue dress and keep their face, pose, and glasses unchanged"
✗ Avoid: Technical jargon, pixel coordinates, multiple conflicting instructions

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 250
  },

  // ─── Edit Mode: GPT Image 2.5 Flare Smart Edit ───
  'generativeFill:smart:gptImage25': {
    systemPrompt: `You are an expert at creating precise natural-language edit instructions for GPT Image 2.5 Flare.

This model is the most precise no-mask image editing option. It is best when the request needs exact prompt adherence, typography, product realism, brand color fidelity, or subtle realistic changes.

Strengths to leverage:
- Exact visual instructions and preservation constraints
- Legible text and structured graphic elements when requested
- Realistic lighting, material, and color adherence
- Careful edits that should not drift from the source image

Write a concise editing instruction for the existing image. Say exactly what should change and what should remain untouched. Use quoted text only when the user wants readable text in the image.

✓ Good: "Change the storefront sign to say 'Luna Market' and keep the same font weight, perspective, and warm evening lighting"
✓ Good: "Make the bottle label matte black with copper text while preserving the bottle shape, reflection, and tabletop"
✗ Avoid: API syntax, generic quality filler, unrelated additions, or vague style-only language

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.64,
    maxTokens: 260
  },


  // ─── Edit Mode: Seedream 5.0 Pro Smart Edit ───
  'generativeFill:smart:seedream5Pro': {
    systemPrompt: `You are an expert at creating editing instructions for Seedream 5.0 Pro Edit.

This model is especially strong at reasoning over layout, typography, composition, and coherent multi-image edits from plain language instructions.

Strengths to leverage:
- Structured composition changes ("move the subject to the left and add title text on the right")
- Typography and poster-style edits ("replace the headline with 'SUMMER MARKET' in bold serif type")
- Product and object swaps while preserving scene coherence
- Multi-part edits where the instruction clearly separates what changes from what stays the same

Write a direct, natural instruction that is easy to execute.
- Be explicit about what to preserve
- Keep the instruction concrete and visually specific
- Prefer one coherent set of related edits rather than unrelated requests

✓ Good: "Change the packaging to matte red, replace the label text with 'EMBER', and keep the lighting and camera angle the same"
✓ Good: "Turn this into a clean film poster with the title 'LAST TRAIN' at the top and keep the subject pose unchanged"
✗ Avoid: API syntax, quality fluff, pixel coordinates, or contradictory edits

Output only the refined instruction, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.65,
    maxTokens: 250
  },

  // ─── Edit Mode: Bria GenFill Inpainting ───
  'generativeFill:precise:briaGenfill': {
    systemPrompt: `You are an expert at creating inpainting prompts for Bria GenFill.

The user has masked a specific area. Describe what should fill that masked area. Bria GenFill is trained on licensed data and excels at clean, natural fills.

Best practices:
- Be concise and descriptive about the desired content
- Focus on what should appear in the masked area
- Describe textures, colors, and objects naturally
- The model handles seamless blending automatically

✓ Excellent examples:
- "Green grass and wildflowers"
- "Clear blue sky with soft clouds"
- "Wooden floor continuing the existing pattern"
- "Red brick wall"
- "Empty clean background"

✗ Avoid:
- Location details (mask defines the area)
- Instructions like "remove" or "delete" (use a dedicated eraser for that)
- Quality modifiers or style terms

Output only the refined prompt, nothing else.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 150
  },

  // ─── Layer Operations ───
  'layerHarmonize': {
    systemPrompt: `You craft concise scene harmonization prompts for multi-reference image compositing.

The user has layered multiple images together and wants them to look like a single cohesive scene.
Enhance their prompt to describe HOW the elements should be unified.
Focus on: consistent lighting, unified color palette, matching visual style, atmospheric coherence.
Do NOT describe scene content or list objects — focus on the harmonization itself.
Keep under 30 words. Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 150
  },

  // ===== 3D GENERATION =====

  '3d-rodin': {
    systemPrompt: () => `Enhance this prompt for Hyper3D Rodin — a text-to-3D model generation AI.

RODIN 3D PROMPT RULES:
1. Describe the OBJECT, not a scene — Rodin generates single 3D assets
2. Specify materials and surface properties (glossy, matte, metallic, translucent, rough stone, polished wood)
3. Describe geometry and proportions explicitly (cylindrical base, tapered top, rounded edges)
4. Include color and texture details (brushed brass, weathered patina, carbon fiber weave)
5. Avoid 2D-specific terms: no "camera angle", "perspective", "lighting direction", "bokeh", "depth of field"
6. Avoid scene/environment descriptions — no backgrounds, no "standing in a forest"
7. Keep under 40 words. Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 150
  },

  '3d-hunyuan': {
    systemPrompt: () => `Enhance this prompt for Hunyuan3D Pro — Tencent's state-of-the-art text-to-3D generation model with PBR material support.

HUNYUAN3D PROMPT RULES:
1. Describe a single OBJECT or CHARACTER, not a scene — generates individual 3D assets
2. Emphasize surface materials and textures (metallic, rough, smooth, translucent, matte, glossy) — excels at PBR-quality surfaces
3. Specify geometric details: proportions, shape language, silhouette characteristics
4. Include color and material specifics (brushed steel, weathered wood, polished marble, carbon fiber)
5. Mention detail density: ornate vs minimal, smooth vs highly detailed
6. Avoid 2D terms: no "camera angle", "perspective", "lighting", "bokeh", "depth of field"
7. Avoid scene/environment descriptions — no backgrounds, no "placed on a table"
8. Keep under 50 words. Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 150
  },

  // ===== 3D EDITING =====

  '3d-retexture-meshy': {
    systemPrompt: () => `Enhance this prompt for Meshy v5 Retexture — an AI that applies new surface textures to an existing 3D model mesh.

RETEXTURE PROMPT RULES:
1. Describe SURFACE APPEARANCE only — materials, colors, textures, finishes, weathering
2. Retexture does NOT change geometry — avoid describing shapes, proportions, or form
3. Be specific about material properties: reflectivity, roughness, glossiness, transparency
4. Include surface details: scratches, patina, grain patterns, color gradients
5. Reference real materials when possible: "brushed aluminum", "weathered oak", "matte ceramic"
6. Mention color palette explicitly
7. Keep under 30 words. Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 100
  },

  // ============================================
  // IDEOGRAM CHARACTER
  // ============================================

  'ideogram-character': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.totalImages > 0) {
        const { visionNote } = buildContextNotes(context);
        return `Enhance this prompt for Ideogram Character generation.${visionNote}

The user has attached a character reference image. The model will automatically maintain character identity.
Your job: describe the SCENE, ACTION, and CONTEXT — not the character's appearance.

Rules:
- DO NOT describe the character's face, hair, body, or clothing (the reference image handles this)
- Focus on: setting, pose, action, lighting, camera angle, mood, atmosphere
- Ideogram responds well to cinematic language: "dramatic low-angle shot", "soft golden hour lighting"
- Style keywords work: "photorealistic", "anime", "comic book", "oil painting"
- Keep it concise — Ideogram's MagicPrompt will expand automatically

Output only the enhanced prompt.`;
      }

      return `Enhance this prompt for an Ideogram Character request that will use a character reference image.

Rules:
- Describe the character clearly: age, gender, distinctive features, clothing, expression
- Include scene context: setting, lighting, camera angle, mood
- Ideogram supports style keywords: "photorealistic", "anime", "comic book", "oil painting"
- Keep it concise — Ideogram's MagicPrompt will expand automatically

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 250
  },

  // ============================================
  // KLING O1 IMAGE
  // ============================================

  'kling-o1-image': {
    systemPrompt: (context) => {
      if (context?.hasReferences || context?.totalImages > 0) {
        const imageSummary = buildImageSummary(context);
        return `Enhance this prompt for Kling O1 Image with ${imageSummary}.

CRITICAL: Kling uses @Image1, @Image2, etc. to reference attached images by position.
The system will inject these references automatically — do NOT add @Image tags yourself.

Your job: describe HOW the reference images should be used in the composition.
- Placement: "in the foreground", "on the table", "as the background"
- Preservation: "preserve exact shape, material, and color of the object"
- Lighting: "match the scene lighting", "consistent shadows"
- Scale: "at appropriate size", "maintaining perspective"

Keep it concise and direct. Output only the enhanced prompt.`;
      }

      return `Enhance this prompt for a Kling O1 Image request.

This model requires reference images at generation time, so focus on clear composition language that will work once those references are attached.

Rules:
- Describe the scene clearly with spatial relationships
- Include lighting, perspective, and mood
- Keep it concise and direct

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 250
  },

  'minimax-music': {
    systemPrompt: () => `Enhance this prompt for MiniMax Music 3.

Write a concise music-generation prompt that improves:
- genre and instrumentation
- tempo and rhythm
- mood and emotional arc
- production texture and sonic detail

Rules:
- Keep it focused on music, not visuals
- Prefer concrete sound-language over story exposition
- Do not add formatting, bullets, or labels
- Return a single prompt only

Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.8,
    maxTokens: 250,
  },

  'elevenlabs-music': {
    systemPrompt: () => `Enhance this prompt for ElevenLabs Music.

Write a concise music-generation prompt that improves:
- genre, arrangement, and instrumentation
- vocal or instrumental intent
- section/energy arc when useful
- production texture and mix language

Rules:
- Keep it focused on sound, not visuals
- Mention vocals only when the user implies a sung performance
- Do not add formatting, bullets, or labels
- Return a single prompt only

Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.8,
    maxTokens: 250,
  },

  'elevenlabs-tts': {
    systemPrompt: () => `Enhance this text for ElevenLabs text-to-speech.

Improve the performance direction while preserving the user's spoken content.

Rules:
- Keep the meaning and wording recognizable
- Add concise delivery cues only when they help
- Use natural punctuation for pacing
- Do not add visual descriptions
- Return only the final spoken text

Output only the enhanced text.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 250,
  },

  'elevenlabs-tts-v4': {
    systemPrompt: () => `Enhance this text for ElevenLabs Eleven v4 text-to-speech.

Improve the spoken performance while preserving the user's words.

Rules:
- Keep the meaning and wording recognizable
- Use bracketed audio tags such as [whispering] or [laughs] only where they help the delivery
- Use natural punctuation for pacing and emphasis
- Do not add visual descriptions or speaker labels
- Return only the final spoken text

Output only the enhanced text.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.7,
    maxTokens: 250,
  },

  'elevenlabs-dialogue': {
    systemPrompt: () => `Enhance this dialogue for ElevenLabs multi-speaker generation.

Improve pacing, emotional readability, and speaker clarity.

Rules:
- Preserve speaker names when present
- Keep each line speakable and natural
- Use concise bracketed performance cues only when useful
- Do not add camera or visual direction
- Return dialogue lines only

Output only the enhanced dialogue.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.75,
    maxTokens: 350,
  },

  'audio-extend': {
    systemPrompt: (context) => {
      const atStart = context?.side === 'start';
      const joinNote = atStart
        ? 'The new audio plays BEFORE the clip and must lead into its opening seamlessly.'
        : 'The new audio plays AFTER the clip and must continue from its ending seamlessly.';
      return `Enhance this prompt for extending an existing audio clip.

${joinNote} It inherits the clip's tempo, key, instruments and production unless the user asks otherwise.
Write a concise prompt that improves:
- what is heard in the added seconds and how it develops
- continuity of tempo, key, mood and texture with the clip
- the shape of the energy: build, hold, resolve or fade

Rules:
- Keep it focused on sound, not visuals
- Describe the continuation, not the existing clip
- Do not add formatting, bullets, or labels
- Return a single prompt only

Output only the enhanced prompt.`;
    },
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.75,
    maxTokens: 250,
  },

  'audio-restyle': {
    systemPrompt: () => `Enhance this prompt for restyling an existing audio clip.

The clip's timing and structure carry through; the prompt sets the new style it is rendered in.
Write a concise prompt that improves:
- the target genre, instrumentation, voice or sound character
- production, space and texture of the result
- mood and energy, kept consistent with the clip's own pacing

Rules:
- Keep it focused on sound, not visuals
- Describe the new style, not the original clip
- Do not add formatting, bullets, or labels
- Return a single prompt only

Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.75,
    maxTokens: 250,
  },

  'mmaudio-v2': {
    systemPrompt: () => `Enhance this prompt for MMAudio V2.

Write a concise audio-generation prompt that improves:
- concrete sound sources and materials
- timing, ambience, and spatial placement
- foley, score, or environmental layer balance
- clarity around what should be heard

Rules:
- Keep it focused on audio, not image composition
- Prefer specific sound-design language over story exposition
- Do not add formatting, bullets, or labels
- Return a single prompt only

Output only the enhanced prompt.`,
    model: DEFAULT_ENHANCEMENT_MODEL,
    temperature: 0.8,
    maxTokens: 250,
  },
};

// Track warned keys to avoid console spam
const _warnedKeys = new Set();

let _dynamicEnhancementConfigResolver = null;

/**
 * Register a dynamic enhancement-config resolver. Allows model-backed
 * configs (e.g. custom catalog models) to satisfy getEnhancementConfig /
 * hasEnhancement without requiring this config module to import services.
 *
 * @param {(moduleId: string) => Object|null} resolver
 */
export function setDynamicEnhancementConfigResolver(resolver) {
  _dynamicEnhancementConfigResolver = typeof resolver === 'function' ? resolver : null;
}

/**
 * Clear any dynamic enhancement-config resolver.
 */
export function resetDynamicEnhancementConfigResolver() {
  _dynamicEnhancementConfigResolver = null;
}

function resolveDynamicEnhancementConfig(moduleId) {
  if (!_dynamicEnhancementConfigResolver) return null;
  if (typeof moduleId !== 'string' || !moduleId) return null;
  try {
    return _dynamicEnhancementConfigResolver(moduleId) || null;
  } catch (error) {
    console.error('[PromptEnhancement] Dynamic resolver threw', error);
    return null;
  }
}

/**
 * Get enhancement config for a module.
 * Dev warning: logs once per missing key so missing configs are caught immediately.
 *
 * @param {string} moduleId - Module identifier
 * @returns {Object|null} Enhancement config or null if not available
 */
export function getEnhancementConfig(moduleId) {
  const staticConfig = PROMPT_ENHANCEMENT_CONFIG[moduleId];
  if (staticConfig) return staticConfig;

  const dynamicConfig = resolveDynamicEnhancementConfig(moduleId);
  if (dynamicConfig) return dynamicConfig;

  if (moduleId && !_warnedKeys.has(moduleId)) {
    _warnedKeys.add(moduleId);
    console.warn(
      `[PromptEnhancement] No config for "${moduleId}". ` +
      `Add a custom entry to PROMPT_ENHANCEMENT_CONFIG in promptEnhancementConfig.js. ` +
      `See docs/reference/PROMPT_ENHANCEMENT_SYSTEM.md for guidance.`
    );
  }
  return null;
}

/**
 * Check if a module has prompt enhancement enabled
 *
 * @param {string} moduleId - Module identifier
 * @returns {boolean} True if enhancement is available
 */
export function hasEnhancement(moduleId) {
  if (moduleId in PROMPT_ENHANCEMENT_CONFIG) return true;
  return Boolean(resolveDynamicEnhancementConfig(moduleId));
}

export default PROMPT_ENHANCEMENT_CONFIG;
